import type { ComponentProps, MapPoint } from '../blocks';
import { categoricalColor, type CategoricalMode } from './categorical';
import { CHART_COLORS, resolveCssColor } from './colors';

/**
 * Shared marker coloring + legend derivation for the `Map` block.
 *
 * One source of truth for how a point becomes a color and how a map's legend is
 * built, so the interactive SPA primitive and the Slack dot rasterizer agree.
 * All colors here are concrete hex (never CSS `var()` tokens): a Mapbox paint
 * expression and the resvg rasterizer both need literal values. The optional
 * `mode` selects the light or dark step of the categorical palette so the SPA can
 * follow the basemap; the Slack rasterizer and the static-image fallback are
 * light-only and take the default.
 */

/** Built-in health colors, matching the Hotspot status palette + KPI tones.
 *  Reserved status hues — a fixed palette, never themed and never a categorical
 *  slot. */
const MAP_STATUS_COLOR: Record<'online' | 'degraded' | 'offline', string> = {
  online: CHART_COLORS.green,
  degraded: CHART_COLORS.orange,
  offline: CHART_COLORS.subtext,
};

/** Canonical health-legend order for the status fallback. */
const STATUS_ORDER = ['online', 'degraded', 'offline'] as const;

/**
 * Gate a model-supplied legend `color` once, here, for every consumer (SPA
 * popup + Mapbox `['get','color']` paint, Slack `mapDotsSvg`, artifact `<img>`
 * fallback). It's the only untrusted color on the block — status/default/palette
 * colors are constants — so run it through `resolveCssColor`'s hex/rgb/hsl
 * allow-list and fall back to the categorical color at `index` on reject, never
 * emitting the raw string (an ungated value like `red; background-image:url(…)`
 * would inject CSS where the SPA interpolates it into `style.cssText`).
 *
 * The gate's own reject value is the primary series color, so pass a ChartColors
 * whose `purple` is a private sentinel: a return of that sentinel means the
 * input failed the allow-list (or was absent), and only then do we use the
 * palette fallback. A valid literal is returned verbatim.
 */
const GATE_REJECT = ' rejected';
const GATE_COLORS = { ...CHART_COLORS, purple: GATE_REJECT };

function safeLegendColor(color: string | undefined, index: number, mode: CategoricalMode): string {
  const fallback = categoricalColor(index, mode);
  if (!color) return fallback;
  const resolved = resolveCssColor(color, GATE_COLORS);
  return resolved === GATE_REJECT ? fallback : resolved;
}

/** One legend entry as it arrives on the block (color optional). */
export type MapLegendEntry = NonNullable<ComponentProps['Map']['legend']>[number];

/** A resolved legend entry for display: color is always concrete. */
export interface MapDisplayLegendEntry {
  key: string;
  label: string;
  color: string;
}

/**
 * Concrete marker color for one point, by precedence:
 *  1. `legend` present -> the entry whose `key === point.group` (its `color`,
 *     else the categorical color at that entry's index); no match -> slot 1.
 *  2. else `point.status` present -> its built-in (reserved) health color.
 *  3. else -> slot 1 (brand purple) — the single-series/ungrouped default.
 */
export function mapPointColor(
  point: Pick<MapPoint, 'group' | 'status'>,
  legend?: MapLegendEntry[],
  mode: CategoricalMode = 'light',
): string {
  if (legend && legend.length > 0) {
    const idx = legend.findIndex((e) => e.key === point.group);
    if (idx < 0) return categoricalColor(0, mode);
    return safeLegendColor(legend[idx].color, idx, mode);
  }
  if (point.status) return MAP_STATUS_COLOR[point.status];
  return categoricalColor(0, mode);
}

/**
 * The legend to display for a block, with concrete colors:
 *  - explicit `legend` if given (palette-filling any entry without a `color`);
 *  - else a health legend of the statuses actually present, in canonical order;
 *  - else empty.
 * Both the SPA and the Slack renderer use this so their legends match.
 */
export function mapDisplayLegend(
  map: ComponentProps['Map'],
  mode: CategoricalMode = 'light',
): MapDisplayLegendEntry[] {
  if (map.legend && map.legend.length > 0) {
    return map.legend.map((e, i) => ({
      key: e.key,
      label: e.label,
      color: safeLegendColor(e.color, i, mode),
    }));
  }
  const present = new Set((map.points ?? []).map((p) => p.status).filter(Boolean));
  return STATUS_ORDER.filter((s) => present.has(s)).map((s) => ({
    key: s,
    label: s[0].toUpperCase() + s.slice(1),
    color: MAP_STATUS_COLOR[s],
  }));
}
